import { VpnKey } from '@mui/icons-material';
import { Box, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface LicenseCardProps {
  used: number;
  total: number;
}

export const LicenseCard = ({ used, total }: LicenseCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const available = Math.max(0, total - used);
  const usagePercent = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  // Determine color based on availability
  const getProgressColor = () => {
    if (used > total) return 'error';
    const availablePercent = 100 - usagePercent;
    if (availablePercent <= 10) return 'error';
    if (availablePercent <= 25) return 'warning';
    return 'primary';
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            color: theme.palette.text.secondary,
            display: 'flex',
            '& svg': {
              fontSize: 28,
            },
          }}
        >
          <VpnKey />
        </Box>
        <Typography component="h2" variant="h5" fontWeight={500} color="text.secondary">
          {t('pages.dashboard.stats.licenses')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {t('pages.dashboard.stats.licenses_available_plural', { count: available })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
        <Typography sx={{ fontSize: 40, fontWeight: 600, lineHeight: 1.2 }}>{used}</Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
          / {total}
        </Typography>
      </Box>
      <LinearProgress
        aria-label={t('pages.dashboard.stats.licenses')}
        variant="determinate"
        value={usagePercent}
        color={getProgressColor()}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.grey[200],
        }}
      />
    </Paper>
  );
};
